/**
 * The unit catalog: what a supplement card's Unit field picks from.
 *
 * Its own file rather than a function on `supplementPlansService`, the way the
 * exercise module keeps its catalog beside the plans it is picked for: this
 * one is cached for the session and read by a form, and the plans it is picked
 * into are fetched per page.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  SupplementUnitOption,
  SupplementUnitsResponse,
} from "../types/supplements.types";

export async function fetchSupplementUnits(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<SupplementUnitOption[]> {
  const token = await getToken();
  if (!token) {
    throw new Error("No active Clerk session - cannot load supplement units.");
  }

  const response = await api.get<SupplementUnitsResponse>(
    API_ENDPOINTS.catalog.supplementUnits,
    { token, signal }
  );

  // Rows without a usable name are dropped rather than shown: an empty option
  // is a dead line in the list, and one nothing could ever be picked from.
  return (response?.units ?? [])
    .filter((row) => typeof row?.name === "string" && row.name.trim())
    .map((row) => ({ id: row.id, name: row.name.trim() }));
}
